import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useErrorHandler } from '../utils/errorHandling';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const { handleError } = useErrorHandler();

  useEffect(() => {
    let unsubscribe;

    const initializeAuth = async () => {
      try {
        // Initialize auth state from localStorage
        const { userData: storedUserData, companyData: storedCompanyData } = authService.initializeAuthState();
        
        if (storedUserData) {
          setUserData(storedUserData);
        }
        
        if (storedCompanyData) {
          setCompany(storedCompanyData);
        }

        // Set up Firebase auth state listener
        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          try {
            if (firebaseUser) {
              setUser(firebaseUser);
              
              // Always refresh the profile from the backend: role, company and
              // permissions change server-side (e.g. an applicant is hired and
              // becomes a driver). The cached copy is only a fallback.
              try {
                console.log('📡 AUTH: Fetching user profile from backend for:', firebaseUser.uid);
                const profile = await authService.getCurrentUserProfile();
                setUserData(profile.user);
                setCompany(profile.company);
              } catch (error) {
                console.warn('❌ AUTH: Failed to fetch user profile:', error.message);
                // Keep the cached profile; the user might still be valid
              }
              
              // Update last login (non-blocking)
              console.log('🔄 AUTH: Updating last login for user:', firebaseUser.uid);
              authService.updateLastLogin(firebaseUser.uid).catch(error => {
                console.warn('Failed to update last login:', error);
              });
            } else {
              setUser(null);
              setUserData(null);
              setCompany(null);
              authService.clearAuthState();
            }
          } catch (error) {
            console.error('Firebase auth state change error:', error);
          } finally {
            setLoading(false);
            setInitialized(true);
          }
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        setLoading(false);
        setInitialized(true);
      }
    };

    initializeAuth();

    // Cleanup subscription
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty dependency array - this should only run once

  useEffect(() => {
    if (!initialized || loading) {
      return;
    }

    if (!user) {
      return;
    }

    const needsProfileRefresh = !userData
      || userData.uid !== user.uid
      || !userData.email;

    if (!needsProfileRefresh) {
      return;
    }

    let isCancelled = false;

    const loadProfile = async () => {
      try {
        const profile = await authService.getCurrentUserProfile();
        if (!isCancelled && profile?.user) {
          setUserData(profile.user);
          setCompany(profile.company || null);
        }
      } catch (error) {
        console.error('Failed to refresh user profile:', error);
      }
    };

    loadProfile();

    return () => {
      isCancelled = true;
    };
  }, [initialized, loading, user, userData]);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      
      const result = await authService.signIn(email, password);
      
      // Firebase user is set automatically by the auth state listener
      // but we need to set the user data and company immediately
      if (result.userData) {
        setUserData(result.userData);
      }
      if (result.company) {
        setCompany(result.company);
      }
      
      return { success: true, user: result.userData, company: result.company };
    } catch (error) {
      const handledError = handleError(error, { context: 'User login' });
      return { 
        success: false, 
        error: handledError.message || 'Login failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const register = useCallback(async (registrationData) => {
    try {
      setLoading(true);
      
      const result = await authService.register(registrationData);
      
      // Firebase user is set automatically by the auth state listener
      // but we need to set the user data and company immediately
      if (result.userData) {
        setUserData(result.userData);
      }
      if (result.company) {
        setCompany(result.company);
      }
      
      return { success: true, user: result.userData, company: result.company };
    } catch (error) {
      const handledError = handleError(error, { context: 'User registration' });
      return { 
        success: false, 
        error: handledError.message || 'Registration failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.signOut();
      
      // Clear state - Firebase auth listener will handle the rest
      setUser(null);
      setUserData(null);
      setCompany(null);
      
      return { success: true };
    } catch (error) {
      const handledError = handleError(error, { context: 'User logout' });
      return { 
        success: false, 
        error: handledError.message || 'Logout failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const resetPassword = useCallback(async (email) => {
    try {
      await authService.resetPassword(email);
      return { success: true };
    } catch (error) {
      const handledError = handleError(error, { context: 'Password reset' });
      return { 
        success: false, 
        error: handledError.message || 'Password reset failed. Please try again.' 
      };
    }
  }, [handleError]);

  const updatePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await authService.updateUserPassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      const handledError = handleError(error, { context: 'Password update' });
      return { 
        success: false, 
        error: handledError.message || 'Password update failed. Please try again.' 
      };
    }
  }, [handleError]);

  const refreshUserProfile = useCallback(async () => {
    try {
      const profile = await authService.getCurrentUserProfile();
      setUserData(profile.user);
      setCompany(profile.company);
      return { success: true, user: profile.user, company: profile.company };
    } catch (error) {
      const handledError = handleError(error, { context: 'Profile refresh' });
      return { 
        success: false, 
        error: handledError.message || 'Failed to refresh profile.' 
      };
    }
  }, [handleError]);

  const isAuthenticated = useCallback(() => {
    return !!user && !!userData;
  }, [user, userData]);

  const hasRole = useCallback((role) => {
    return userData?.role === role;
  }, [userData]);

  const hasPermission = useCallback((permission) => {
    if (!userData) {
      return false;
    }

    // Check explicit permission first
    if (userData.permissions && userData.permissions[permission] !== undefined) {
      return Boolean(userData.permissions[permission]);
    }

    const permissionAliases = {
      canViewAllDrivers: 'drivers',
      canEditDocuments: 'documents',
      canDeleteDocuments: 'documents',
      canManageUsers: 'users',
      canViewReports: 'reports',
    };

    const rolePermissions = {
      super_admin: ['dashboard', 'fleet', 'vehicles', 'drivers', 'routes', 'loads', 'financial', 'compliance', 'reports', 'settings', 'users', 'companies', 'documents'],
      admin: ['dashboard', 'fleet', 'vehicles', 'drivers', 'routes', 'loads', 'financial', 'compliance', 'reports', 'settings', 'users', 'documents'],
      manager: ['dashboard', 'fleet', 'vehicles', 'drivers', 'routes', 'loads', 'financial', 'compliance', 'reports', 'documents'],
      dispatcher: ['dashboard', 'fleet', 'vehicles', 'routes', 'loads', 'drivers'],
      mechanic: ['dashboard', 'vehicles', 'maintenance'],
      driver: ['dashboard'],
      viewer: ['dashboard'],
      applicant: ['apply']
    };

    const normalizedPermission = permissionAliases[permission] || permission;

    return rolePermissions[userData.role]?.includes(normalizedPermission) || false;
  }, [userData]);

  const hasAnyRole = useCallback((roles) => {
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  const getPermissionLevel = useCallback(() => {
    const roleHierarchy = {
      super_admin: 7,
      admin: 6,
      manager: 5,
      dispatcher: 4,
      mechanic: 3,
      driver: 2,
      viewer: 1,
      applicant: 1
    };

    return roleHierarchy[userData?.role] || 0;
  }, [userData]);

  const value = {
    // State
    user,
    currentUser: user, // Legacy alias used throughout onboarding flow
    userData,
    userProfile: userData, // Legacy alias expected by older components
    company,
    loading,
    initialized,
    
    // Authentication methods
    login,
    signIn: login, // Alias for backward compatibility
    register,
    signUp: register, // Alias for backward compatibility
    logout,
    signOut: logout, // Alias for backward compatibility
    resetPassword,
    updatePassword,
    refreshUserProfile,
    
    // Authorization methods
    isAuthenticated,
    hasRole,
    hasPermission,
    hasAnyRole,
    hasAnyPermission,
    getPermissionLevel
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
